import React from "react";

export type LinearStepperStep = {
  id: string | number;
  label: string;
};

export type LinearStepperProps = {
  steps: LinearStepperStep[];
  currentStep: number;
  /** Highest step index that can be jumped to (completed/green). */
  maxNavigableStep?: number;
  onStepClick?: (stepIndex: number) => void;
  className?: string;
};

export default function LinearStepper({
  steps,
  currentStep,
  maxNavigableStep,
  onStepClick,
  className = "",
}: LinearStepperProps) {
  return (
    <div
      className={["flex w-full items-center", className].filter(Boolean).join(" ")}
    >
      {steps.map((step, index) => {
        const isActive = index === currentStep;
        const isCompleted =
          typeof maxNavigableStep === "number"
            ? index <= maxNavigableStep && index !== currentStep
            : index < currentStep;
        const isClickable =
          typeof maxNavigableStep === "number"
            ? index <= maxNavigableStep
            : index <= currentStep;

        return (
          <React.Fragment key={step.id}>
            <button
              type="button"
              onClick={() => (isClickable ? onStepClick?.(index) : undefined)}
              disabled={!isClickable || !onStepClick}
              className={[
                "inline-flex items-center gap-3",
                isClickable && onStepClick ? "cursor-pointer" : "cursor-default",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-current={isActive ? "step" : undefined}
            >
              <span
                className={[
                  "inline-flex h-8 w-8 items-center justify-center rounded-full border text-md font-medium transition-colors",
                  isActive
                    ? "border-black bg-bright-green text-dark-black"
                    : isCompleted
                      ? "border-bright-green bg-bright-green text-dark-black"
                      : "border-light-grey bg-medium-grey text-dark-grey",
                ].join(" ")}
              >
                {index + 1}
              </span>
              <span
                className={[
                  "hidden whitespace-nowrap text-sm font-medium sm:inline",
                  isClickable ? "text-dark-black" : "text-dark-grey",
                ].join(" ")}
              >
                {step.label}
              </span>
            </button>

            {index < steps.length - 1 ? (
              <div className="mx-1 h-px min-w-4 flex-1 bg-light-grey sm:mx-4 sm:min-w-10" aria-hidden="true" />
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}
